import * as React from 'react';
import { ClickToScrollOffsets } from '@tock/hooks';
import { DynamicTabsListNavigation } from 'ui/DynamicTabs/DynamicTabsListNavigation';
import { Grid } from 'ui';
import { IconButton, SearchInput, Typography } from 'omakase';
import { IconMigrationUtil } from 'omakase/utils/IconMigrationUtil';
import { IconSearch } from '@tock/icons';
import { IconSearch as LegacyIconSearch } from 'ui/icons';
import { core } from '@tock/coretypes';
import { defaultMemoize } from 'reselect';
import { menuFilterRootStyles } from 'components/business/menu/menuFilterCssDefs';
import { useMediaQuery } from '@material-ui/core';
import { useTheme } from '@emotion/react';
import Fuse from 'fuse.js';
import _ from 'lodash';

const TRANSITION_DURATION = 150;

interface Props extends ClickToScrollOffsets {
  value?: number;
  optionGroups: core.ITicketOptionGroup[];
  onChangeSearch(value: string): void;
  container?: React.ReactInstance | (() => React.ReactInstance | null) | null;
  /**
   * id selector when looking for a scroll target
   */
  selector?: (value: number) => string;
}

const MenuFilter: React.FunctionComponent<Props> = ({
  container,
  offset,
  offsetSm,
  offsetMd,
  onChangeSearch,
  optionGroups,
  selector,
  value,
}) => {
  const theme = useTheme();
  const [isSearchBarOpen, setIsSearchBarOpen] = React.useState(false);
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const matchesSm = useMediaQuery(theme.breakpoints.up('sm'));

  const handleSearchIconClick = () => {
    setIsSearchBarOpen(true);
    setTimeout(() => {
      searchInputRef.current?.focus();
    }, TRANSITION_DURATION + 100);
  };
  const handleCloseClick = () => {
    onChangeSearch('');
    setIsSearchBarOpen(false);
  };
  const handleSearchChange = (value: string) => onChangeSearch(value);

  return (
    <Grid container css={menuFilterRootStyles}>
      <Grid item xs={12}>
        <Grid container alignItems="center" spacing={2}>
          {(!isSearchBarOpen || matchesSm) && (
            <Grid item xs={10} sm={isSearchBarOpen ? 6 : 10}>
              <Typography component="h1" variant="heading04" data-testid="menu-title">
                Menu
              </Typography>
            </Grid>
          )}

          <Grid
            container
            justify="flex-end"
            item
            xs={isSearchBarOpen ? 12 : 2}
            sm={isSearchBarOpen ? 6 : 2}
          >
            {isSearchBarOpen ? (
              <SearchInput
                fullWidth
                ref={searchInputRef}
                aria-label="Search menu"
                data-testid="menu-search-input"
                placeholder="Search"
                onChange={handleSearchChange}
                onClear={handleCloseClick}
              />
            ) : (
              <Grid container justify="flex-end" item xs={12}>
                <IconButton
                  variant="ghost"
                  onClick={handleSearchIconClick}
                  aria-label="Search menu"
                  size="medium"
                >
                  <IconMigrationUtil legacyIcon={<LegacyIconSearch />} newIcon={<IconSearch />} />
                </IconButton>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>

      {optionGroups.length > 0 && (
        <Grid item xs={12}>
          <DynamicTabsListNavigation<number>
            tabs={optionGroups.map((optionGroup) => {
              return {
                label: optionGroup.name ?? '',
                text: optionGroup.name ?? '',
                value: optionGroup.id ?? -1,
              };
            })}
            value={value ?? optionGroups[0].id ?? -1}
            offset={offset}
            offsetSm={offsetSm}
            offsetMd={offsetMd}
            container={container}
            selector={selector}
          />
        </Grid>
      )}
    </Grid>
  );
};

export const createOptionGroupFilterer = defaultMemoize(
  (optionGroups: core.ITicketOptionGroup[]) => {
    return new Fuse(optionGroups, {
      shouldSort: false,
      minMatchCharLength: 2,
      threshold: 0.1,
      keys: ['name'],
    });
  }
);

export const createOptionFilterer = (options: core.ITicketOption[]) => {
  return new Fuse(options, {
    shouldSort: false,
    minMatchCharLength: 2,
    threshold: 0.3,
    keys: ['name', 'description'],
  });
};

export function getTopLevelOptionGroupsWithFilter(
  searchText: string,
  topLevelOptionGroups: core.ITicketOptionGroup[]
) {
  const optionGroups = _.isEmpty(searchText)
    ? topLevelOptionGroups
    : _.map(createOptionGroupFilterer(topLevelOptionGroups).search(searchText), (result) => {
        return result.item;
      });
  // Adding this return check defensively
  return _.filter(optionGroups, (optionGroup) => !optionGroup.parentTicketOptionId);
}

export function getTopLevelOptionsWithFilter(
  searchText: string,
  ticketOption: core.ITicketOption[]
) {
  return _.isEmpty(searchText)
    ? ticketOption
    : _.map(createOptionFilterer(ticketOption).search(searchText), (result) => {
        return result.item;
      });
}

export function getUnionSetOfOptionGroupsToDisplay(
  allTicketOptionGroups: core.ITicketOptionGroup[],
  topLevelOptionGroupsWithFilter: core.ITicketOptionGroup[],
  ticketOptionsWithFilter: core.ITicketOption[]
): core.ITicketOptionGroup[] {
  const optionGroupIdsForTicketOptionsWithFilter = new Set(
    _.filter(
      _.map(ticketOptionsWithFilter, (option) => option.ticketOptionGroupId),
      (id) => !!id
    )
  );
  return _.filter(
    allTicketOptionGroups,
    (optionGroup) =>
      topLevelOptionGroupsWithFilter.includes(optionGroup) ||
      optionGroupIdsForTicketOptionsWithFilter.has(optionGroup.id)
  );
}

export default MenuFilter;
